import { JsonTree } from '../../components/JsonTree';
import { Anchor, Breadcrumbs, Button, Card, Grid, Group, Tabs, Text } from '@mantine/core';
import { IconBuilding, IconHash, IconMail, IconPencil, IconPhone, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import Spinner from '../../components/Spinner';
import formatDate from '../../constants/formattedDate';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import CompaniesDetailsAggregatedStatsPage from './CompaniesDetailsAggregatedStatsPage';
import CompaniesDetailsEventsPage from './CompaniesDetailsEventsPage';
import CompaniesDetailsProfilesPage from './CompaniesDetailsProfilesPage';

const page = `${Page['companies']}`;

const CompaniesDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();

  const [deletePerformed, setDeletePerformed] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [company, setCompany] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const { accountId, domainId, subpage, id } = useParams();
  const { domain } = useSelector((state) => state.domain);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/companies/${encodeURIComponent(id)}`)
      .then(({ company }) => {
        setCompany({
          ...company,
          timestamp: formatDate(company.timestamp),
        });
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, id]);

  const del = (cb) => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('authorization', `Bearer ${domain.secretKey}`);

    fetch(`/api/v0/companies/${company.companyId}`, {
      method: 'DELETE',
      headers,
    })
      .then(() => {
        setDeletePerformed(true);
        if (typeof cb === 'function') {
          cb();
        }
      })
      .catch(setUserError);
  };

  const onSelect = (subpath) => {
    return navigate(`/accounts/${accountId}/domains/${domainId}/companies/${encodeURIComponent(id)}/${subpath}`);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!company) {
    return <Spinner />;
  }

  let title = `${page}`;
  if (company?.name) {
    title += ` | ${company.name}`;
  }
  title += ' | Details';

  return (
    <>
      <Metadata title={title} />

      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/companies?page=1`}>{page}</Anchor>
        <Text>Details</Text>
      </Breadcrumbs>
      <Card mb="sm" withBorder>
        <Card.Section inheritPadding py="sm">
          <div className="card-title">
            <IconBuilding size={16} /> {company.name}
          </div>
          <hr />
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }} ta="left">
              <div>
                <IconHash size={14} /> {company.companyId}
              </div>
              <div>
                <IconMail size={14} />{' '}
                {company.phoneNumber ? <a href={`mailto:${company.email}`}>{company.email}</a> : 'N/A'}
              </div>
              <div>
                <IconPhone size={14} />{' '}
                {company.phoneNumber ? <a href={`tel:${company.phoneNumber}`}>{company.phoneNumber}</a> : 'N/A'}
              </div>

              <ErrorMessage message={userError?.message} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <JsonTree
                defaultExpanded={true}
                maxDepth={3}
                data={JSON.parse(
                  JSON.stringify({
                    ...company,
                    name: undefined,
                    email: undefined,
                    phoneNumber: undefined,
                    companyId: undefined,
                    tags: undefined,
                  })
                )}
              />
            </Grid.Col>
          </Grid>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Group gap="sm" justify="flex-end">
            <Button
              size="sm"
              component="a"
              href={`#/accounts/${accountId}/domains/${domainId}/companies/${encodeURIComponent(id)}/edit`}
            >
              <IconPencil size={14} /> Edit
            </Button>
            <Button size="sm" color="red" onClick={() => setDeleteConfirmationOpen(true)}>
              <IconTrash size={14} /> Delete
            </Button>
          </Group>
          {deletePerformed && <Text>Company has been scheduled for deletion.</Text>}
        </Card.Section>
        <ConfirmationModal
          show={deleteConfirmationOpen}
          close={() => setDeleteConfirmationOpen(false)}
          confirm={(cb) => del(cb)}
          message="This action cannot be undone!"
        />
      </Card>

      <Tabs m={0} value={subpage} onChange={onSelect}>
        <Tabs.List>
          <Tabs.Tab value="events">Recent Events</Tabs.Tab>
          <Tabs.Tab value="profiles">Profiles</Tabs.Tab>
          <Tabs.Tab value="aggregatedStats" data-tour="companyDetailsAggregatedStatsContent">
            Aggregated Statistics
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="events">
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <CompaniesDetailsEventsPage trackingId={id} />
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="profiles">
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <CompaniesDetailsProfilesPage trackingId={id} />
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="aggregatedStats">
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <CompaniesDetailsAggregatedStatsPage trackingId={id} />
            </Card.Section>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default CompaniesDetailsPage;
